import type { Session } from "@opensea/auth"
import { authFetch, fetchSession } from "@opensea/auth"
import type { EVMAddress } from "@opensea/utils"
import {
  createSiwxMessage,
  type ExchangeProps,
  parseSiwxMessage,
  SiweAdapter,
} from "@opensea/wallet"
import Cookies from "js-cookie"
import { ENVIRONMENT, LOG_LEVEL } from "@/constants/env"

const AUTH_HINT_COOKIE_NAME = "auth_hint"

type ResponseData = {
  nonce: string
}

type AdapterOptions = {
  domain?: string
}

class OSSiweAdapter extends SiweAdapter {
  private readonly domain: string | undefined

  constructor({ domain }: AdapterOptions = {}) {
    super()
    this.domain = domain
  }

  async getNonce(): Promise<string> {
    const response = await authFetch("/auth/siwe/nonce", {
      method: "POST",
    })
    const responseJson: ResponseData = (await response.json()) as ResponseData
    return responseJson.nonce
  }

  async getWhitelisted(address: string) {
    const response = await authFetch("/auth/fut/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ address }),
    })
    const responseJson = (await response.json()) as {
      success: boolean
    }
    return responseJson.success
  }

  async verify({
    message,
    signature,
    chainArch,
    connectorId,
  }: {
    message: string
    signature: string
    chainArch: string
    connectorId?: string
  }) {
    const jsonMessage = parseSiwxMessage(message)
    const response = await authFetch("/auth/siwe/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: jsonMessage,
        signature,
        chainArch,
        connectorId,
      }),
    })

    return response.json() as Promise<Session>
  }

  async endSession() {
    await authFetch("/auth/session/logout", {
      method: "POST",
    })
  }

  async getSession() {
    let session: Session | undefined = await fetchSession({ debug: LOG_LEVEL })

    if (session === undefined) {
      if (this.tryRefresh) {
        session = await this.tryRefresh()
      }
    }

    return session
  }

  override createMessage({
    nonce,
    address,
    chainId,
    statement = "Click to sign in and accept the OpenSea Terms of Service (https://opensea.io/tos) and Privacy Policy (https://opensea.io/privacy).",
  }: CreateMessageProps) {
    const currentUrl = new URL(window.location.href)
    const baseUri = this.domain
      ? `https://${this.domain}${currentUrl.pathname}`
      : `${currentUrl.origin}${currentUrl.pathname}`

    return createSiwxMessage({
      version: "1",
      domain: this.domain ?? window.location.host,
      uri: encodeURI(baseUri),
      address: address as EVMAddress,
      chainId,
      nonce,
      statement,
    })
  }

  override async exchangeToken({ token }: ExchangeProps) {
    const response = await authFetch("/auth/exchange/privy", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    })

    return response.json() as Promise<Session>
  }

  async tryRefresh?() {
    // Check for auth_hint cookie first
    const authHint = Cookies.get(AUTH_HINT_COOKIE_NAME)
    if (authHint !== "true") {
      return undefined
    }

    const response = await fetch("/__api/auth/session/refresh", {
      method: "POST",
    })

    if (!response.ok) {
      switch (LOG_LEVEL) {
        case "silent":
          return undefined
        default:
          console.error("Failed to refresh session")
          return undefined
      }
    }

    return response.json() as Promise<Session>
  }
}

// TODO: Export from @opensea/wallet
type CreateMessageProps = Parameters<
  typeof SiweAdapter.prototype.createMessage
>[0]

export const siweAdapter = new OSSiweAdapter({
  domain: ENVIRONMENT === "development" ? "opensea.io" : undefined,
})
